import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button as ButtonMUI,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import {
  EditOutlined as EditIcon,
  EmailOutlined as EmailIcon,
  LocalHospital as HospitalIcon,
  LocationOn as LocationOnIcon,
  PeopleOutlined as PeopleIcon,
  PhoneOutlined as PhoneIcon,
  StoreOutlined as StoreIcon,
} from '@material-ui/icons';
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ErrorIconOutlined from '@material-ui/icons/ErrorOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import clsx from 'clsx';
import { CheckBox, Input } from 'components/atoms';
import SearchableSelect from 'components/mocules/SearchableSelect';
import { BRAND_NAME, ENUM_SCOPE } from 'constants/Enums';
import useLeadSources from 'hooks/useLeadSources';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { Controller, useForm } from 'react-hook-form';
import AddressService from 'services/AddressService';
import { LANG_DEFAULT } from 'sysconfig';
import { NotifyUtils, ValidateUtils, regUtils } from 'utils';
import { getRegisterURL } from 'utils/registerURL';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import useMobileV2 from 'zustand-lib/storeMobile';
import AddressSelect from 'components-v2/mocules/SelectArea';
import styles from './styles.module.css';

const { isEmpty, validatePhone } = ValidateUtils;

// update to zustand for country
const { EMAIL_REGEX } = regUtils;

const LIST_PROVINCE_CODE_NEED_FILL_DISTRICT_WARD = ['10'];

const EMAIL_MIN_LENGTH = 6;
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 30;

// Lead source option whose selection shows the free-text `otherSourceDetail` input
const LEAD_SOURCE_OTHER = 'other';

const NewSignUpForm = React.memo((props) => {
  const { t: translatePopupAction, i18n } = useTranslation('popup-action');
  const { t: translateCommon } = useTranslation('common');
  const [showPassword, setShowPassword] = useState(false);
  const {
    className,
    onClickSignIn,
    onClickSignUp,
    referCode,
    page,
    setPage,
    handleScrollTop,
    variant,
  } = props;
  // Layout theo variant (thứ tự field giống nhau ở mọi chế độ: tên, email, SĐT, mật khẩu, địa chỉ,
  // tên doanh nghiệp, loại hình, "How did you find us", (ô "Khác"), mã giới thiệu):
  // - variant="hero": 1 cột theo design homepage banner (SectionHero), vùng field cuộn được + footer CTA cố định.
  // - variant="fullscreen": popup full màn hình trên mobile web (PopupFullScreen), luôn 1 cột kể cả tablet.
  // - mặc định (NewSignUpModal): lưới 2 cột từ md trở lên, 1 cột dưới md.
  const isHero = variant === 'hero';
  const isSingleColumn = isHero || variant === 'fullscreen';
  const colFull = isSingleColumn ? 'col-span-1' : 'col-span-1 md:col-span-2';
  const colHalf = 'col-span-1';
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState([]);
  const beta = useMobileV2((state) => state.beta);
  const router = useRouter();
  const formRef = useRef(null);

  // Lead source ("How did you find us"): optional. Only rendered when the API returns a non-empty list;
  // while loading / on error / non-OK / empty list the field is hidden and never blocks registration.
  const { leadSources } = useLeadSources();
  const hasLeadSources = leadSources.length > 0;
  const currentLocale = i18n?.language || router?.locale || LANG_DEFAULT;
  const getLeadSourceLabel = (source) => source?.labels?.[currentLocale] || source?.label;
  const leadSourceOptions = useMemo(
    () => leadSources.map((source) => ({ value: source.value, label: getLeadSourceLabel(source) })),
    [leadSources, currentLocale],
  );
  const scopeOptions = useMemo(
    () =>
      Object.keys(ENUM_SCOPE)
        .filter((item) => item !== 'NEW_CUSTOMER')
        .map((item) => ({ value: item, label: translateCommon(`scope.${item}`) })),
    [translateCommon],
  );

  const schema = yup
    .object({
      name: yup
        .string()
        .required(translatePopupAction('popup_register.validate_require_username'))
        .min(2, translatePopupAction('popup_register.validate_username_min_2_word')),
      businessName: yup
        .string()
        .trim()
        .required(translatePopupAction('popup_register.MISSING_BUSINESS_NAME'))
        .max(255, translatePopupAction('popup_register.validate_business_name_max'))
        .matches(
          /^[\p{L}\p{M}\p{N}\s]+$/u,
          translatePopupAction('popup_register.validate_business_name_format'),
        ),
      password: yup
        .string()
        .required(translatePopupAction('popup_register.validate_password_empty'))
        .min(
          PASSWORD_MIN_LENGTH,
          translatePopupAction('popup_register.password_must_be_entered_min2_and_max30_character'),
        )
        .max(
          PASSWORD_MAX_LENGTH,
          translatePopupAction('popup_register.password_must_be_entered_min2_and_max30_character'),
        ),
      phone: yup
        .string()
        .required(translatePopupAction('popup_register.not_entered_your_phoneNumber'))
        // dùng chung rule với ValidateUtils.validatePhone để chỉ có 1 nguồn sự thật theo ENV_COUNTRY
        .test(
          'phone-format',
          translatePopupAction('popup_register.phone_not_correct_format'),
          (value) => isEmpty(value) || validatePhone(value),
        ),
      // email là field optional: bỏ trống thì bỏ qua, có nhập thì mới check format
      email: yup
        .string()
        .test(
          'email-format',
          translatePopupAction('popup_register.email_not_correct_format'),
          (value) =>
            isEmpty(value) || (value.length >= EMAIL_MIN_LENGTH && EMAIL_REGEX.test(value)),
        ),
      // dob: yup.date().required(translatePopupAction('popup_register.not_entered_your_dob')), // because optional field
      provinceCode: yup
        .string()
        .required(translatePopupAction('popup_register.not_entered_your_address')),
      // districtCode: yup.string().required(translatePopupAction('popup_register.not_entered_your_dob')),
      // wardCode: yup.string().required(translatePopupAction('popup_register.not_entered_your_dob')),
      scope: yup
        .string()
        .required(translatePopupAction('popup_register.not_entered_your_business_type')),
      isCheckAgree: yup
        .bool()
        .required(translatePopupAction('popup_register.message_agree_terms'))
        .oneOf([true], translatePopupAction('popup_register.message_agree_terms')),
    })
    .required();
  const {
    formState: { errors },
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      businessName: '',
      phone: '',
      email: '',
      password: '',
      scope: '',
      isCheckAgree: false,
      provinceCode: '',
      leadSource: '',
      otherSourceDetail: '',
    },
  });
  const selectedLeadSource = watch('leadSource');

  useEffect(() => {
    const getListProvince = async () => {
      try {
        const provinceResult = await AddressService.getProvinces();
        setProvinces(provinceResult);
      } catch (error) {
        NotifyUtils.error('Can not get list province');
      }
    };

    getListProvince();
  }, []);

  // useEffect(() => {
  //   if (LIST_PROVINCE_CODE_NEED_FILL_DISTRICT_WARD.includes(watch('provinceCode'))) {
  //     const getListDistrict = async () => {
  //       try {
  //         const districtResult = await AddressService.getDistrictsByProvince(watch('provinceCode'));
  //         setDistricts(districtResult);
  //         setValue('districtCode', '');
  //         setValue('wardCode', '');
  //       } catch (error) {
  //         NotifyUtils.error('Can not get list district');
  //       }
  //     };
  //     getListDistrict();
  //   }
  // }, [watch('provinceCode')]);

  // useEffect(() => {
  //   if (watch('districtCode')) {
  //     console.log('call get ward');
  //     const getListWard = async () => {
  //       try {
  //         const wardResult = await AddressService.getWardsByDistrict(watch('districtCode'));
  //         setWards(wardResult);
  //         setValue('wardCode', '');
  //       } catch (error) {
  //         NotifyUtils.error('Can not get list ward');
  //       }
  //     };
  //     getListWard();
  //   }
  // }, [watch('districtCode')]);

  // toàn bộ rule validate đã nằm trong yup schema, tới được đây là data đã hợp lệ
  const handleSubmitSignUp = useCallback(
    (data) => {
      // Lead tracking: registerURL is always sent (backend reads UTM from it; last UTM landing URL
      // of the session when the current URL has no UTM, see utils/registerURL);
      // leadSource only when the customer picked one (optional field),
      // otherSourceDetail only when leadSource is "other" and the detail is not blank.
      const { leadSource, otherSourceDetail, ...restData } = data;
      const signUpData = { ...restData, registerURL: getRegisterURL() };
      if (hasLeadSources && leadSource) {
        signUpData.leadSource = leadSource;
        const trimmedDetail = (otherSourceDetail || '').trim();
        if (leadSource === LEAD_SOURCE_OTHER && trimmedDetail) {
          signUpData.otherSourceDetail = trimmedDetail;
        }
      }
      onClickSignUp(signUpData);
    },
    [onClickSignUp, hasLeadSources],
  );

  // scroll tới field lỗi đầu tiên theo thứ tự nhìn thấy trên màn hình.
  // dựa vào vị trí thực tế (getBoundingClientRect) thay vì thứ tự DOM/thứ tự key trong errors,
  // vì popup desktop sắp lại ô otherSourceDetail bằng CSS order và key trong errors không đảm bảo thứ tự.
  const scrollToFirstError = useCallback(() => {
    window.requestAnimationFrame(() => {
      const form = formRef.current;
      if (!form) return;

      const errorNodes = Array.from(form.querySelectorAll(`.${styles.text_error}`));
      if (errorNodes.length === 0) return;

      const firstErrorNode = errorNodes.reduce((topNode, node) =>
        node.getBoundingClientRect().top < topNode.getBoundingClientRect().top ? node : topNode,
      );

      const fieldNode = firstErrorNode.closest('.MuiFormControl-root') || firstErrorNode;
      fieldNode.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // [role="combobox"]: trigger của SearchableSelect (loại hình kinh doanh)
      const focusableNode = fieldNode.querySelector(
        'input:not([type="hidden"]), textarea, [role="combobox"]',
      );
      if (focusableNode) focusableNode.focus({ preventScroll: true });
    });
  }, []);

  const handleSetAddressValue = useCallback(
    (name, value) => {
      setValue(name, value, { shouldValidate: true });
    },
    [setValue],
  );

  const handleClickSignIn = useCallback(
    (e) => {
      e.preventDefault();
      onClickSignIn();
    },
    [onClickSignIn],
  );

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickTerms = useCallback(
    (e) => {
      e.preventDefault();
      router.push('/?terms=true');
      setPage(page + 2);
      handleScrollTop();
    },
    [page],
  );

  const IconAccount = (
    <InputAdornment position="start" className={styles.input_icon}>
      <AccountCircleOutlinedIcon />
    </InputAdornment>
  );
  const IconPassword = (
    <InputAdornment position="start" className={styles.input_icon}>
      <LockOutlinedIcon />
    </InputAdornment>
  );
  const IconOtherSource = (
    <InputAdornment position="start" className={styles.input_icon}>
      <EditIcon />
    </InputAdornment>
  );

  const IconPhone = (
    <InputAdornment position="start" className={styles.input_icon}>
      <PhoneIcon />
    </InputAdornment>
  );

  const IconLocation = (
    <InputAdornment position="start" className={styles.input_icon}>
      <LocationOnIcon />
    </InputAdornment>
  );
  const IconEndPassword = (
    <InputAdornment position="end" className={styles.input_icon}>
      <IconButton onClick={handleClickShowPassword}>
        {!showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
      </IconButton>
    </InputAdornment>
  );
  const IconInviter = (
    <InputAdornment position="start" className={styles.input_icon}>
      <PeopleIcon />
    </InputAdornment>
  );
  const IconEmail = (
    <InputAdornment position="start" className={styles.input_icon}>
      <EmailIcon />
    </InputAdornment>
  );
  const IconStore = (
    <InputAdornment position="start" className={styles.input_icon}>
      <StoreIcon />
    </InputAdornment>
  );

  const labelAgree = (
    <div>
      {translatePopupAction('popup_register.agree_policy')}
      &nbsp;
      {beta && isMobile ? (
        <Box component="a" onClick={handleClickTerms} style={{ color: '#005C29' }}>
          {translatePopupAction('popup_register.policy')}
        </Box>
      ) : (
        <a
          href="/terms-and-condition"
          target="_blank"
          rel="noreferrer"
          style={{ color: '#005C29' }}
        >
          {translatePopupAction('popup_register.policy')}
        </a>
      )}
      {/* <a href="/terms-and-condition" target="_blank" rel="noreferrer" style={{ color: '#005C29' }}>
        Điều khoản sử dụng*
      </a> */}
    </div>
  );

  const isInvalidForm = Object.keys(errors).length > 0;

  useEffect(() => {
    if (referCode) {
      setValue('referCode', referCode);
    }
  }, [referCode, setValue]);

  return (
    <div className={clsx(className, styles.form_sign_up, isHero && styles.form_hero)}>
      <div
        className={clsx('flex items-center', isHero ? 'flex-wrap px-4 pt-4 lg:pt-6 mb-4' : 'mb-5')}
      >
        <p className={clsx('mr-2', isHero ? 'text-xl font-medium' : 'text-base md:text-xl')}>
          {translatePopupAction('popup_register.sign_up_today_for')}
        </p>
        <p
          className={clsx(
            'text-[#FF9E1F]',
            isHero ? 'text-xl font-semibold' : 'text-base md:text-2xl font-bold',
          )}
        >
          {translatePopupAction('popup_register.exclusive_discount')}
        </p>
      </div>
      <form
        ref={formRef}
        className={clsx(className, isHero && styles.form_hero_body)}
        onSubmit={handleSubmit(handleSubmitSignUp, scrollToFirstError)}
      >
        <div className={clsx(isHero && styles.fields_area)}>
          <div
            className={clsx(
              'grid',
              isSingleColumn ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2',
              isInvalidForm ? 'gap-2' : 'gap-4',
            )}
          >
            <div className={colHalf}>
              <Controller
                name="name"
                control={control}
                className={styles.form_control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Input
                      startAdornment={IconAccount}
                      placeholder={translatePopupAction('popup_register.username')}
                      variant="outlined"
                      error={!!errors.name || false}
                      className={styles.input}
                      data-test="signup-username"
                      {...field}
                    />
                    {errors.name?.message && (
                      <div className={styles.text_error} data-test="signup-err-username">
                        {errors.name?.message}
                      </div>
                    )}
                  </FormControl>
                )}
              />
            </div>
            <div className={colHalf}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Input
                      startAdornment={IconEmail}
                      placeholder={translatePopupAction('popup_register.email')}
                      variant="outlined"
                      error={!!errors.email || false}
                      className={styles.input}
                      data-test="signup-email"
                      {...field}
                    />
                    {errors.email?.message && (
                      <div className={styles.text_error} data-test="signup-err-email">
                        {errors.email?.message}
                      </div>
                    )}
                  </FormControl>
                )}
              />
            </div>

            <div className={colHalf}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Input
                      startAdornment={IconPhone}
                      placeholder={translatePopupAction('popup_register.phone_number')}
                      variant="outlined"
                      error={!!errors.phone || false}
                      className={styles.input}
                      data-test="signup-phone"
                      {...field}
                    />
                    {errors.phone?.message && (
                      <div className={styles.text_error} data-test="signup-err-phone">
                        {errors.phone?.message}
                      </div>
                    )}
                  </FormControl>
                )}
              />
            </div>
            <div className={colHalf}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="password"
                      startAdornment={IconPassword}
                      endAdornment={IconEndPassword}
                      placeholder={translatePopupAction('popup_register.password')}
                      variant="outlined"
                      error={!!errors.password || false}
                      className={styles.input}
                      data-test="signup-password"
                      {...field}
                    />
                    {errors.password?.message && (
                      <div className={styles.text_error} data-test="signup-err-password">
                        {errors.password?.message}
                      </div>
                    )}
                  </FormControl>
                )}
              />
            </div>

            {/* <div className='md:col-span-2 col-span-3'>
              <Controller
                name="provinceCode"
                control={control}
                render={({ field }) => (
                  <FormControl margin="none" fullWidth>
                    <Select
                      key={uuidv4()}
                      className={styles.container_select}
                      name="provinceCode"
                      variant="outlined"
                      IconComponent={ExpandMoreIcon}
                      input={<Input data-test="signup-provinceCode" startAdornment={IconLocation} />}
                      {...field}
                    >
                      {provinces.map((province) => (
                        <MenuItem value={province.value} key={uuidv4()}>
                          {province.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </div> */}

            <div className={colFull}>
              <FormControl margin="none" fullWidth>
                <AddressSelect
                  error={errors.provinceCode || false}
                  // validate lại ngay khi chọn địa chỉ để xoá text lỗi đang hiển thị
                  setValue={handleSetAddressValue}
                  className={styles.form_control}
                  placeholder={translatePopupAction('popup_register.address')}
                  data-test="signup-address"
                  classNameInput={styles.input}
                  icon={IconLocation}
                  provinces={provinces}
                />
                {errors.provinceCode?.message && (
                  <div className={styles.text_error} data-test="signup-err-address">
                    {errors.provinceCode?.message}
                  </div>
                )}
              </FormControl>
            </div>

            {/* {LIST_PROVINCE_CODE_NEED_FILL_DISTRICT_WARD.includes(watch('provinceCode')) && (
              <>
                <Controller
                  name="districtCode"
                  control={control}
                  render={({ field }) => (
                    <FormControl margin="none" fullWidth>
                      <Select
                        key={uuidv4()}
                        className={styles.container_select}
                        name="districtCode"
                        variant="outlined"
                        IconComponent={ExpandMoreIcon}
                        input={<Input data-test="signup-districtCode" startAdornment={IconLocation} />}
                        {...field}
                      >
                        {districts.map((province) => (
                          <MenuItem value={province.value} key={uuidv4()}>
                            {province.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                {watch('districtCode') && (
                  <Controller
                    name="wardCode"
                    control={control}
                    render={({ field }) => (
                      <FormControl margin="none" fullWidth>
                        <Select
                          key={uuidv4()}
                          className={styles.container_select}
                          name="wardCode"
                          variant="outlined"
                          IconComponent={ExpandMoreIcon}
                          input={<Input data-test="signup-wardCode" startAdornment={IconLocation} />}
                          {...field}
                        >
                          {wards.map((ward) => (
                            <MenuItem value={ward.value} key={uuidv4()}>
                              {ward.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                )}
              </>
            )} */}

            <div className={colHalf}>
              <Controller
                name="businessName"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Input
                      startAdornment={IconStore}
                      placeholder={translatePopupAction('popup_register.business_name')}
                      variant="outlined"
                      error={!!errors.businessName || false}
                      className={styles.input}
                      inputProps={{ maxLength: 255 }}
                      data-test="signup-businessName"
                      {...field}
                    />
                    {errors.businessName?.message && (
                      <div className={styles.text_error} data-test="signup-err-businessName">
                        {errors.businessName?.message}
                      </div>
                    )}
                  </FormControl>
                )}
              />
            </div>
            <div className={colHalf}>
              <Controller
                name="scope"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <SearchableSelect
                      ref={field.ref}
                      id="signup-scope"
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      options={scopeOptions}
                      startIcon={<HospitalIcon />}
                      placeholder={translatePopupAction('popup_register.choose_your_business_type')}
                      searchPlaceholder={translatePopupAction(
                        'popup_register.search_business_type',
                      )}
                      noResultsText={translatePopupAction('popup_register.no_results')}
                      error={!!errors.scope}
                      data-test="signup-scope"
                    />
                    {errors.scope?.message && (
                      <div className={styles.text_error} data-test="signup-err-scope">
                        {errors.scope?.message}
                      </div>
                    )}
                  </FormControl>
                )}
              />
            </div>

            {hasLeadSources && (
              <div className={colHalf}>
                <Controller
                  name="leadSource"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <SearchableSelect
                        ref={field.ref}
                        id="signup-leadSource"
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        options={leadSourceOptions}
                        startIcon={<CampaignOutlinedIcon />}
                        placeholder={translatePopupAction(
                          'popup_register.lead_source_placeholder',
                          {
                            brandname: BRAND_NAME,
                          },
                        )}
                        searchPlaceholder={translatePopupAction(
                          'popup_register.search_lead_source',
                        )}
                        noResultsText={translatePopupAction('popup_register.no_results')}
                        data-test="signup-leadSource"
                      />
                    </FormControl>
                  )}
                />
              </div>
            )}

            {/* ô "Khác": ngay sau "How did you find us" khi 1 cột; popup desktop 2 cột đẩy xuống
                cuối lưới (md:order-last) để nằm dưới hàng [How did you find us | Referral], chiếm cả hàng */}
            {hasLeadSources && selectedLeadSource === LEAD_SOURCE_OTHER && (
              <div className={clsx(colFull, !isSingleColumn && 'md:order-last')}>
                <Controller
                  name="otherSourceDetail"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Input
                        startAdornment={IconOtherSource}
                        placeholder={translatePopupAction(
                          'popup_register.other_source_detail_placeholder',
                        )}
                        variant="outlined"
                        className={styles.input}
                        data-test="signup-otherSourceDetail"
                        {...field}
                      />
                    </FormControl>
                  )}
                />
              </div>
            )}

            {/* ô mã giới thiệu nằm cạnh "How did you find us"; khi field lead source ẩn thì chiếm cả hàng */}
            <div className={hasLeadSources ? colHalf : colFull}>
              <Controller
                name="referCode"
                defaultValue={referCode}
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Input
                      startAdornment={IconInviter}
                      value={referCode}
                      placeholder={translatePopupAction('popup_register.referral_number')}
                      variant="outlined"
                      // disabled={!!referCode}
                      className={styles.input}
                      data-test="signup-referCode"
                      {...field}
                    />
                  </FormControl>
                )}
              />
            </div>
          </div>

          <div className="col-span-3">
            <Controller
              name="isCheckAgree"
              defaultValue={false}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <div className={styles.agree_term}>
                    <CheckBox data-test="signup-isCheckAgree" label={labelAgree} {...field} />
                  </div>
                  {errors.isCheckAgree?.message && (
                    <div className={styles.text_error} data-test="signup-err-isCheckAgree">
                      {errors.isCheckAgree?.message}
                    </div>
                  )}
                </FormControl>
              )}
            />
          </div>
        </div>

        <div className={clsx(isHero && styles.footer_hero)}>
          <div className="col-span-3">
            <Box className={styles.center} style={{ whiteSpace: 'nowrap' }}>
              <ButtonMUI className={styles.btn_register} type="submit" data-test="signup-submit">
                {translatePopupAction('popup_register.create_account')}
              </ButtonMUI>
            </Box>
          </div>

          {/* <Typography className={styles.disclaimer}>
            {translatePopupAction('popup_register.message_confirm_signup_account').replace('{BRAND_NAME}', BRAND_NAME)}
          </Typography> */}
          <div className={`${styles.link_login_wrapper} ${styles.center} col-span-3`}>
            <p>
              {translatePopupAction('popup_register.have_account')}
              &nbsp;
              <a href="#top" className={styles.link_login} onClick={handleClickSignIn}>
                {translatePopupAction('popup_register.login')}
              </a>
            </p>
          </div>
        </div>
      </form>
    </div>
  );
});

export default NewSignUpForm;
